/**
 * The site scheduling calendar surface — a thin wrapper around the MUI X
 * Scheduler that renders one site's relay schedule as day/week/month/agenda
 * views with relays as resources.
 *
 * Two things the scheduler does not offer out of the box are handled here:
 *
 *  - Click interception. The community scheduler has no click callback and its
 *    built-in edit dialog cannot express relay on/off timing, so a capture-phase
 *    listener catches clicks (and keyboard activation) on an event chip anywhere
 *    in the document — including the portaled "+N more" overflow popover —
 *    suppresses the built-in dialog, and raises our own via onEventActivate. The
 *    chip is identified by an occurrence-id class the event adapter stamps on it.
 *    Native drag/resize are pointer-driven (no trailing click), so they survive.
 *  - Per-relay color. The scheduler palette is a fixed 11-name set, so each
 *    relay's true hex is injected as CSS variables on its color class, scoped by
 *    the always-present `[data-palette]` attribute to outrank the library's own
 *    palette rule.
 *
 * Recurrence never reaches the scheduler: events are pre-expanded occurrences,
 * so no `rrule` prop is ever passed (that would trigger the Premium recurrence
 * plugin and scope dialog).
 *
 * Contains:
 * - SiteEventCalendar — the calendar component (default export)
 */
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import { EventCalendar } from '@mui/x-scheduler/event-calendar';
import React, { useCallback, useEffect, useMemo } from 'react';

import {
  diffEventChange,
  findEventIdFromTarget,
  MONTH_CELL_MIN_HEIGHT,
  relayColorClassName,
  relayColorCssVars,
} from '@sophocles/views/sites/utils/schedulerEventAdapter';

const CALENDAR_VIEWS = ['day', 'week', 'month', 'agenda'];
const ACTIVATION_KEYS = new Set(['Enter', ' ']);
const MIN_CALENDAR_HEIGHT = 640;

/**
 * @param {object} props
 * @param {object[]} props.events - Pre-expanded scheduler events (from the adapter).
 * @param {object[]} props.resources - Relay resource rows.
 * @param {number[]} props.relayNumbers - Relay numbers present, for color CSS.
 * @param {string} props.displayTimezone - Site IANA timezone.
 * @param {('day'|'week'|'month'|'agenda')} props.view - Controlled view.
 * @param {Date} props.visibleDate - Controlled visible date.
 * @param {?number} props.weekStartsOn - date-fns week start (0 = Sunday).
 * @param {(view: string) => void} props.onViewChange
 * @param {(date: Date) => void} props.onVisibleDateChange
 * @param {(occurrenceId: string, domEvent: Event) => void} props.onEventActivate
 * @param {(intent: object) => void} props.onEventDragResize - Receives the diff intent.
 * @param {boolean} props.readOnly - Prevents drag/resize writes while the site's command queue is occupied.
 */
export default function SiteEventCalendar({
  events,
  resources,
  relayNumbers,
  displayTimezone,
  view,
  visibleDate,
  weekStartsOn,
  onViewChange,
  onVisibleDateChange,
  onEventActivate,
  onEventDragResize,
  readOnly = false,
}) {
  const colorStyles = useMemo(() => {
    const styles = {};
    (relayNumbers ?? []).forEach(relayNumber => {
      styles[`.${relayColorClassName(relayNumber)}[data-palette]`] = relayColorCssVars(relayNumber);
    });
    return styles;
  }, [relayNumbers]);

  const defaultPreferences = useMemo(() => (weekStartsOn != null ? { weekStartsOn } : undefined), [weekStartsOn]);

  useEffect(() => {
    const intercept = domEvent => {
      const id = findEventIdFromTarget(domEvent.target);
      if (!id) {
        return;
      }
      domEvent.stopImmediatePropagation();
      domEvent.preventDefault();
      onEventActivate(id, domEvent);
    };
    const interceptKey = domEvent => {
      if (ACTIVATION_KEYS.has(domEvent.key)) {
        intercept(domEvent);
      }
    };
    document.addEventListener('click', intercept, true);
    document.addEventListener('keydown', interceptKey, true);
    return () => {
      document.removeEventListener('click', intercept, true);
      document.removeEventListener('keydown', interceptKey, true);
    };
  }, [onEventActivate]);

  const handleEventsChange = useCallback(
    nextEvents => {
      if (readOnly) return;
      const intent = diffEventChange(events, nextEvents);
      if (intent) {
        onEventDragResize(intent);
      }
    },
    [events, onEventDragResize, readOnly],
  );

  return (
    <>
      <GlobalStyles styles={colorStyles} />
      <Box
        sx={{
          height: '100%',
          minHeight: MIN_CALENDAR_HEIGHT,
          display: 'flex',
          '& .MuiEventCalendar-monthViewBody': { gridAutoRows: `minmax(${MONTH_CELL_MIN_HEIGHT}px, 1fr)` },
        }}
      >
        <EventCalendar
          events={events}
          resources={resources}
          views={CALENDAR_VIEWS}
          view={view}
          onViewChange={onViewChange}
          visibleDate={visibleDate}
          onVisibleDateChange={onVisibleDateChange}
          displayTimezone={displayTimezone}
          eventCreation={false}
          defaultPreferences={defaultPreferences}
          onEventsChange={readOnly ? undefined : handleEventsChange}
          style={{ flex: 1 }}
        />
      </Box>
    </>
  );
}
